import { Form } from 'antd';
import { HeaderContent, ManageProductComponent, showAlertNotification } from 'components/common';
import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCheckUniqueMutation, useCreateProductMutation } from 'services';
import { Attribute, ImagePayload, ProductPayload, Unit, ProductSalePrice, TableEnum } from 'types';

const CreateProductPage: React.FC = () => {
  const [form] = Form.useForm();
  const [createProduct, { isLoading }] = useCreateProductMutation();
  const [checkUnique] = useCheckUniqueMutation();
  const navigate = useNavigate();
  const payloadRef = useRef<ProductPayload | null>(null);
  const handleSubmitForm = async (
    {
      productUnits,
      productAttributes,
      imagesData,
      productSalePrices,
      productSuppliers,
    }: {
      productUnits: Unit[];
      productAttributes: Attribute[];
      imagesData: ImagePayload[];
      productSalePrices?: ProductSalePrice[];
      productSuppliers?: string[];
    },
    isPublish: boolean = false,
    publishStatusId?: string,
    isActive: boolean = true,
    setModalOpen?: React.Dispatch<React.SetStateAction<boolean>>,
    setStatusSave?: React.Dispatch<React.SetStateAction<string>>,
  ) => {
    try {
      if (isPublish) {
        setStatusSave?.('bản công khai');
        await form.validateFields();
      } else {
        setStatusSave?.('bản nháp');
      }
      productUnits = productUnits
        .filter((unit) => unit.checked === true)
        .map((unit) => {
          if (unit.isNew) {
            return {
              ...unit,
              unitId: undefined,
              name: unit.label || '',
            };
          }
          return unit;
        });
      productAttributes = productAttributes.map((attr) => ({
        ...attr,
        salePrice: attr.salePrice || 0,
      }));
      const values = await form.getFieldsValue();

      const payload: ProductPayload = {
        code: values.code,
        name: values.name,
        categoryId: values.category,
        statusId: publishStatusId,
        brandId: values.brand,
        color: values.color,
        dimension: values.dimension,
        sizeSpec: values.sizeSpec,
        costPrice: Number(values.costPrice),
        salePrice: Number(values.salePrice),
        description: values.describeProduct,
        baseUnitId: values.baseUnitId,
        currencyUnitId: values.currencyUnitId,
        minQuantity: Number(values.min),
        maxQuantity: Number(values.max),
        density: Number(values.density),
        barcode: values.barcode,
        productAttributes,
        productUnits,
        productSalePrices,
        productSuppliers: productSuppliers?.map((supplierId) => ({ supplierId })) || [],
        imagesInfo: imagesData.map(({ filePath, name, order }) => ({ filePath, name, order })),
        isPublishing: isPublish,
        isActive: isActive,
      };
      payloadRef.current = payload;
      console.log('Payload for product creation:', payload);
      setModalOpen?.(true);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };
  const handleConfirmCreate = async () => {
    if (!payloadRef.current) return;
    try {
      checkUnique({
        table: TableEnum.Products,
        field: 'code',
        value: payloadRef.current.code,
      }).then((response) => {
        if (response?.data?.data.exists) {
          form.setFields([
            {
              name: 'code',
              errors: [`Mã sản phẩm "${payloadRef.current?.code}" đã tồn tại. Vui lòng sử dụng mã khác.`],
            },
          ]);
          showAlertNotification({
            type: 'error',
            title: 'Lỗi',
            message: `Mã sản phẩm "${payloadRef.current?.code}" đã tồn tại. Vui lòng sử dụng mã khác.`,
          });
          return;
        }
      });
      const res = await createProduct(payloadRef.current).unwrap();
      if (res.success) {
        form.resetFields();
        if (payloadRef.current.isPublishing) {
          showAlertNotification({
            type: 'success',
            title: 'Công khai sản phẩm thành công',
            message: `Sản phẩm bạn vừa công khai có mã: ${res?.data?.product?.code} được tô nền màu xanh trên đầu bảng.`,
          });
        } else {
          showAlertNotification({
            type: 'success',
            title: 'Tạo mới thành công',
            message: `Sản phẩm bạn vừa tạo có mã: ${res?.data?.product?.code} được tô nền màu xanh trên đầu bảng.`,
          });
        }
        payloadRef.current = null;
        navigate('/items/products?newChange=true');
      }
    } catch (error) {
      console.error('Create product failed:', error);
      showAlertNotification({
        type: 'error',
        title: 'Lỗi',
        message: `Vui lòng nhập đúng thông tin yêu cầu.`,
      });
    }
  };
  const handleCancelCreate = async (setModalOpen?: React.Dispatch<React.SetStateAction<boolean>>) => {
    setModalOpen?.(false);
    payloadRef.current = null;
  };
  return (
    <>
      <HeaderContent
        size='sm'
        key='confirm-create-product'
        label={'Tạo Sản Phẩm Mới'}
        contentHeader={'Bây giờ, bạn hãy điền toàn bộ các thông tin cơ bản bên dưới để hoàn thành một sản phẩm mới'}
      />
      <ManageProductComponent
        isLoading={isLoading}
        form={form}
        onSubmit={handleSubmitForm}
        onConfirm={handleConfirmCreate}
        onCancel={handleCancelCreate}
      />
    </>
  );
};

export default CreateProductPage;
